'use client';
import { auth, signOut } from '@fe-common/firebase/auth/auth';
import { getDocById } from '@fe-common/firebase/get-doc-by-id';
import { getOrgId } from '@fe-common/local-ids/get-org-id';
import { Org } from '@pop-common/objects/org/org';
import { Geist, Geist_Mono } from 'next/font/google';
import { useRouter } from 'next/navigation';
import { PrimeReactProvider } from 'primereact/api';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Toolbar } from 'primereact/toolbar';
import { useState } from 'react';
import { useAsync } from 'react-use';
import { ToastProvider } from './contexts/ToastContext';
import './globals.css';

const geistSans = Geist({
  variable: '--font-geist-sans',
  subsets: ['latin'],
});

const geistMono = Geist_Mono({
  variable: '--font-geist-mono',
  subsets: ['latin'],
});

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const router = useRouter();
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const { value: orgDisplayName } = useAsync(async () => {
    if (!auth.currentUser) return;
    const orgId = await getOrgId();
    if (!orgId) return undefined;
    const org = await getDocById<Org>({ id: orgId!, Deserializer: Org, skipCache: true });
    return org?.getDisplayName();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    router.push('/login');
  };

  const handleSidebarNav = (route: string) => {
    setSidebarVisible(false);
    router.push(route);
  };

  const leftContents = (
    <Button
      icon="pi pi-bars"
      rounded
      text
      aria-label="Menu"
      onClick={() => setSidebarVisible(true)}
      severity="secondary"
      className="mr-2"
    />
  );

  const rightContents = (
    <Button label="Logout" icon="pi pi-sign-out" link severity="secondary" onClick={handleLogout} />
  );

  return (
    <html lang="en">
      <body className={`${geistSans.variable} ${geistMono.variable} antialiased`}>
        <PrimeReactProvider>
          <ToastProvider>
            <Sidebar visible={sidebarVisible} onHide={() => setSidebarVisible(false)}>
              <div className="flex flex-col h-full justify-between">
                <div>
                  <h2 className="text-xl font-bold mb-4">Welcome {orgDisplayName ?? ''}</h2>
                  <Button
                    label="Dashboards"
                    icon="pi pi-chart-line"
                    className="w-full justify-start mb-2"
                    link
                    severity="secondary"
                    onClick={() => handleSidebarNav('/dashboards')}
                  />
                  <Button
                    label="Explore"
                    icon="pi pi-compass"
                    className="w-full justify-start mb-2"
                    link
                    severity="secondary"
                    onClick={() => handleSidebarNav('/explore')}
                  />
                  <Button
                    label="Experience Editor"
                    icon="pi pi-pencil"
                    className="w-full justify-start mb-2"
                    link
                    severity="secondary"
                    onClick={() => handleSidebarNav('/experience-editor')}
                  />
                  <Button
                    label="Experience Manager"
                    icon="pi pi-list"
                    className="w-full justify-start mb-2"
                    link
                    severity="secondary"
                    onClick={() => handleSidebarNav('/experience-manager')}
                  />
                  <Button
                    label="Install"
                    icon="pi pi-download"
                    className="w-full justify-start mb-2"
                    link
                    severity="secondary"
                    onClick={() => handleSidebarNav('/install')}
                  />
                  {/* Add more sidebar items here as needed */}
                </div>
                <Button
                  label="Logout"
                  icon="pi pi-sign-out"
                  link
                  severity="secondary"
                  onClick={handleLogout}
                  className="mt-auto"
                />
              </div>
            </Sidebar>
            <Toolbar className="mb-4" start={leftContents} end={rightContents} />
            <div>{children}</div>
          </ToastProvider>
        </PrimeReactProvider>
      </body>
    </html>
  );
}
